import { useState, useMemo } from "react";
import { Search, ClipboardCopy, FolderOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { usePrescriptionTemplates, usePrescriptionTemplateGroups, type PrescriptionTemplate } from "@/lib/queries";
import { bucketTemplates } from "@/lib/templateGroups";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onPick: (template: PrescriptionTemplate) => void;
}

/**
 * Searchable picker for stored prescription templates, opened by the
 * "Templates" button in the EMR visit page's prescription card (placed
 * before "Copy old prescription"). Picking a template hands its medicine
 * rows to the parent, which APPENDS them to the current prescription
 * (serialNo continues from the end) — the same persist path as copy-old.
 *
 * Templates are shown as per-group accordions ("Ungrouped" pinned last).
 * ALL accordions start COLLAPSED (user request 2026-11-02) — the doctor
 * opens a group manually. The filter matches the template name OR any
 * medicine name ("sertraline" surfaces every protocol containing it) and
 * PRESERVES the accordion structure: only groups holding a match render
 * (empty buckets are dropped by bucketTemplates), each still collapsed
 * until opened. No flat keyboard-nav list — the user opens and picks.
 */
export function PrescriptionTemplatePickerDialog({ open, onOpenChange, onPick }: Props) {
  const { data, isLoading } = usePrescriptionTemplates();
  const { data: groupsData } = usePrescriptionTemplateGroups();
  const [query, setQuery] = useState("");
  // Accordion expansion: starts EMPTY — every accordion (named groups and
  // "Ungrouped") renders COLLAPSED until the user opens it (user request
  // 2026-11-02). Controlled state persists across typing/search-clears
  // within one dialog session; reset on dialog close.
  const [expanded, setExpanded] = useState<string[]>([]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const groups = useMemo(() => groupsData?.rows ?? [], [groupsData]);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return rows;
    return rows.filter((t) =>
      t.name.toLowerCase().includes(q) ||
      t.medicines.some((m) => m.name.toLowerCase().includes(q)));
  }, [rows, q]);

  // Group buckets over the FILTERED list (shared with the management
  // page): searching keeps the accordions — only groups holding a match
  // render, each still collapsed; the user opens one and picks a template.
  const grouped = useMemo(() => bucketTemplates(filtered, groups), [filtered, groups]);

  // Reset expansion + search when the dialog closes.
  const reset = () => { setExpanded([]); setQuery(""); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-maroon-700 dark:text-amber-500">Prescription templates</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <Input
            autoFocus
            className="pl-9"
            placeholder="Search template or medicine name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <ScrollArea className="h-[50vh] rounded-lg border border-cream-200 dark:border-dark-700">
          <div className="p-2">
            {isLoading ? (
              <div className="space-y-2 p-1">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
            ) : grouped.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted">
                {rows.length === 0
                  ? "No templates stored yet — create them from the Prescription Templates page or the “Save as template” button."
                  : "No templates match your search."}
              </div>
            ) : (
              <Accordion
                type="multiple"
                value={expanded}
                onValueChange={setExpanded}
                className="space-y-1"
              >
                {grouped.map((b) => (
                  <AccordionItem key={b.key} value={b.key} className="border border-cream-200 rounded-lg px-3 dark:border-dark-700">
                    <AccordionTrigger className="py-2.5 text-left text-sm font-medium text-maroon-700 hover:no-underline dark:text-amber-500">
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <FolderOpen size={15} className="shrink-0 text-maroon-600 dark:text-amber-500" />
                        <span className="min-w-0 break-words text-left">{b.name}</span>
                        <span className="shrink-0 whitespace-nowrap text-xs font-normal text-muted">
                          {b.items.length} template{b.items.length === 1 ? "" : "s"}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-1.5">
                      {b.items.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          className="flex w-full items-start gap-2.5 rounded-lg border border-cream-200 bg-white p-2.5 text-left transition-colors hover:bg-cream-50/50 dark:border-dark-700 dark:bg-dark-800"
                          onClick={() => { onPick(t); onOpenChange(false); }}
                        >
                          <ClipboardCopy size={15} className="mt-0.5 shrink-0 text-maroon-600 dark:text-amber-500" />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-baseline justify-between gap-2">
                              <span className="min-w-0 break-words text-sm font-medium text-ink dark:text-cream-50">{t.name}</span>
                              <span className="shrink-0 whitespace-nowrap text-xs text-muted">{t.medicines.length} medicine{t.medicines.length === 1 ? "" : "s"}</span>
                            </span>
                            <span className="mt-0.5 block space-y-0.5 text-xs text-muted">
                              {t.medicines.map((m, i) => {
                                // Abbreviated dose summary — the previous-visit
                                // preview format (MedicinePreview / CopyPreviousRxDialog):
                                // name + strength inline, then frequency · duration
                                // (taper steps condensed to "1-1-1×15d" joins).
                                const hasTaper = !!m.taper && m.taper.length > 0;
                                const dose = hasTaper
                                  ? `Taper (${m.taper!.length} periods) · ${m.taper!.map((s) => `${s.frequency}×${s.days}d`).join(", ")}`
                                  : [m.frequency, m.duration].filter(Boolean).join(" · ");
                                return (
                                  <span key={i} className="block break-words">
                                    {m.name}{m.strength ? ` ${m.strength}` : ""}{dose ? ` — ${dose}` : ""}
                                  </span>
                                );
                              })}
                            </span>
                          </span>
                        </button>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </ScrollArea>
        <p className="text-xs text-muted">
          Selecting a template appends its medicines to the current prescription.
        </p>
      </DialogContent>
    </Dialog>
  );
}